import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Lock, KeyRound, ScrollText, Activity, ShieldCheck, Award, Globe, HeartPulse,
  Brain, Building2, GitBranch, Database, Server, FileSearch, Cpu, ArrowRight,
  ChevronDown, Mail, FileText, ShieldAlert, Workflow, Eye, Users, RefreshCw,
  Trash2, Check, BookLock, Network, BadgeCheck, HardDrive, X, Loader2, CheckCircle2,
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { Dialog, DialogContent } from './ui/dialog';

// Trust Center — Swiss Minimalist + Pastel theme (mirrors Home / Features).
// Pastels: sage #D4E0DA · peach #EBD8C8 · lavender #D9CFD9 · accent blue #0F62FE
const TINTS = ['#D4E0DA', '#EBD8C8', '#D9CFD9'];

const SECURITY_PRINCIPLES = [
  { icon: Lock, title: 'Encryption', desc: 'Data is protected using industry-standard encryption for data in transit and at rest.' },
  { icon: KeyRound, title: 'Access Control', desc: 'Role-based access controls help ensure users only access the information necessary for their responsibilities.' },
  { icon: ScrollText, title: 'Audit Logging', desc: 'Security-relevant actions are tracked and recorded to support visibility, accountability, and compliance.' },
  { icon: Activity, title: 'Continuous Monitoring', desc: 'Ongoing monitoring helps organizations maintain visibility into compliance posture and security controls.' },
];

const FRAMEWORKS = [
  { name: 'SOC 2', icon: ShieldCheck },
  { name: 'ISO 27001', icon: Award },
  { name: 'GDPR', icon: Globe },
  { name: 'HIPAA', icon: HeartPulse },
];

const AI_GOV = [
  {
    icon: Cpu, title: 'How Nova Uses AI',
    desc: 'Nova applies AI to accelerate the work of your compliance team across the program lifecycle:',
    bullets: ['Compliance analysis', 'Control mapping', 'Evidence review', 'Gap identification', 'Remediation guidance', 'Compliance workflow automation'],
  },
  { icon: BookLock, title: 'Customer Data Ownership', desc: 'Your data remains customer-owned and under your control at all times. Nova processes it solely to deliver the service you have configured.' },
  { icon: Eye, title: 'AI Privacy Principles', desc: 'We are committed to protecting customer information and maintaining transparency around how AI-assisted workflows operate and what they produce.' },
  { icon: Users, title: 'Human Oversight', desc: 'Customers retain full review and decision-making authority over AI-generated outputs and recommendations — AI assists, people decide.' },
  { icon: ShieldCheck, title: 'Responsible AI Governance', desc: 'Nova is committed to secure, explainable, and accountable AI practices throughout the platform.' },
  { icon: BadgeCheck, title: 'AI Compliance Readiness', desc: 'Nova helps organizations prepare for evolving AI governance and privacy requirements as the regulatory landscape matures.' },
];

const INTEGRATIONS = [
  {
    icon: Building2, name: 'Google Workspace',
    bullets: ['Directory and security posture visibility', 'Secure authentication', 'Least-privilege access principles', 'Administrative oversight'],
  },
  {
    icon: GitBranch, name: 'GitHub',
    bullets: ['Repository security posture visibility', 'Branch protection review', 'Security alert visibility', 'Secure integration management'],
  },
];

const DATA_HANDLING = [
  { icon: ShieldCheck, title: 'Data Protection', desc: 'Customer data is safeguarded throughout its lifecycle using layered controls and encryption.' },
  { icon: Server, title: 'Secure Storage', desc: 'Compliance-related information is stored using secure storage practices designed to protect confidentiality and integrity.' },
  { icon: ScrollText, title: 'Data Retention', desc: 'Retention follows configurable, policy-driven practices aligned to your compliance and business requirements.' },
  { icon: HardDrive, title: 'Backup & Recovery', desc: 'Systems are designed to support data resiliency, with backup and recovery practices that promote continuity.' },
  { icon: Trash2, title: 'Data Deletion', desc: 'Customer-requested deletion processes and data lifecycle management help you stay in control of your information.' },
];

const ARCH_LAYERS = [
  { icon: Network, title: 'Secure Integration Layer', desc: 'Connects external systems through encrypted channels and securely managed credentials with least-privilege scopes.' },
  { icon: Server, title: 'Nova Platform', desc: 'The orchestration core — role-based access, audit logging, and tenant isolation govern every action.' },
  { icon: Cpu, title: 'AI Compliance Engine', desc: 'Analyzes evidence, maps controls, and surfaces gaps with explainable, human-reviewable recommendations.' },
  { icon: Database, title: 'Evidence Repository', desc: 'A traceable, tamper-evident store linking evidence to controls and frameworks for defensible audits.' },
  { icon: Workflow, title: 'Reporting, Monitoring & Remediation', desc: 'Continuous monitoring, dashboards, and workflow automation keep your compliance posture current.' },
];

const ARCH_HIGHLIGHTS = [
  'Encrypted data transmission', 'Secure credential management', 'Role-based access controls',
  'Audit logging', 'Least-privilege access', 'Evidence traceability',
  'Continuous monitoring', 'Compliance workflow automation',
];

const POLICIES = [
  { icon: FileText, title: 'Privacy Policy', desc: 'How we collect, use, and protect personal data.', to: '/privacy-policy' },
  { icon: ScrollText, title: 'Terms of Service', desc: 'The terms that govern use of the Nova platform.', to: '/terms-of-service' },
  { icon: BookLock, title: 'Data Processing Agreement (DPA)', desc: 'Our commitments for processing customer data.', to: '/dpa' },
  { icon: Mail, title: 'Security Contact', desc: 'Reach our security team for questions or reports.', href: 'mailto:security@teamnova.ai' },
];

const FAQS = [
  { q: 'How does Nova secure customer data?', a: 'Nova protects data with industry-standard encryption in transit and at rest, role-based access controls, audit logging, and continuous monitoring. Access follows least-privilege principles and security-relevant actions are recorded for accountability.' },
  { q: 'Does Nova use customer data to train AI models?', a: 'Customer data remains customer-owned and under your control. Nova uses AI to assist with compliance workflows for your organization, and customers retain review and decision-making authority over AI-generated outputs.' },
  { q: 'What integrations does Nova support?', a: 'Nova securely connects with Google Workspace and GitHub today, with additional integrations on the roadmap. Integrations use secure authentication, minimal required permissions, and secure credential handling.' },
  { q: 'How does Nova support compliance programs?', a: 'Nova automates evidence collection, control mapping, gap identification, and remediation tracking across leading security, privacy, and AI governance frameworks — helping you operationalize compliance and prepare for independent audits.' },
  { q: 'How can I request security documentation?', a: 'Use the "Request Documentation" button above or email security@teamnova.ai. Our security team will follow up with the appropriate materials for your review.' },
  { q: 'How can I report a vulnerability?', a: 'If you believe you have identified a potential security vulnerability, please contact security@teamnova.ai. We appreciate responsible disclosure and work to investigate and address reports appropriately.' },
];

const DOC_OPTIONS = ['Security Overview', 'Compliance Coverage', 'Data Processing Agreement', 'General Information'];

const Eyebrow = ({ children }) => (
  <div className="flex items-center justify-center gap-3 mb-5">
    <span className="h-px w-8 bg-[#0F62FE]/45 dark:bg-[#5C97FF]/45" />
    <p className="font-label text-[13px] font-semibold uppercase tracking-[0.22em] text-[#0F62FE] dark:text-[#5C97FF]">{children}</p>
    <span className="h-px w-8 bg-[#0F62FE]/45 dark:bg-[#5C97FF]/45" />
  </div>
);

const Trust = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);
  const [docOpen, setDocOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ full_name: '', company_name: '', work_email: '', role_title: '', documentation_requested: DOC_OPTIONS[0] });

  // SEO metadata for /trust (title + meta + Open Graph + Twitter cards)
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Nova Trust Center | Security, Privacy & Compliance';
    const desc = 'Learn how Nova secures customer data, manages integrations, supports compliance programs, and enables responsible AI governance.';
    const keywords = 'Nova Trust Center, Nova Security, Compliance Automation, AI Governance, SOC 2, ISO 27001, GDPR, HIPAA, Privacy, Security';
    const url = (typeof window !== 'undefined' ? window.location.origin : 'https://teamnova.ai') + '/trust';
    const tags = [
      { name: 'description', content: desc },
      { name: 'keywords', content: keywords },
      { property: 'og:title', content: 'Nova Trust Center | Security, Privacy & Compliance' },
      { property: 'og:description', content: desc },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: url },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Nova Trust Center | Security, Privacy & Compliance' },
      { name: 'twitter:description', content: desc },
    ];
    const created = [];
    tags.forEach(({ name, property, content }) => {
      const sel = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement('meta'); if (name) el.setAttribute('name', name); else el.setAttribute('property', property); document.head.appendChild(el); created.push(el); }
      el.setAttribute('data-trust-seo', 'true');
      el.setAttribute('content', content);
    });
    let canonical = document.head.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonical;
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    const prevCanonical = canonical.getAttribute('href');
    canonical.setAttribute('href', url);
    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
      if (canonicalCreated) canonical.remove(); else if (prevCanonical) canonical.setAttribute('href', prevCanonical);
    };
  }, []);

  const openDocModal = () => { setSubmitted(false); setDocOpen(true); };

  const submitDocRequest = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.work_email.trim()) { toast.error('Please provide your name and work email.'); return; }
    setSubmitting(true);
    try {
      await axios.post('/api/trust/documentation-request', form);
      setSubmitted(true);
      setForm({ full_name: '', company_name: '', work_email: '', role_title: '', documentation_requested: DOC_OPTIONS[0] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] font-body" data-testid="trust-page">
      <Header />

      {/* ===================== HERO ===================== */}
      <section
        className="bg-[#D4E0DA]/55 dark:bg-[#101F18]/55 pt-24 md:pt-32 pb-24 md:pb-32 px-6 border-b border-black/10 dark:border-white/10"
        data-testid="trust-hero"
      >
        <div className="max-w-5xl mx-auto text-center w-full">
          <div className="inline-flex items-center gap-2 mb-8 px-3 py-1.5 rounded-full border border-black/10 dark:border-white/15 bg-white/60 dark:bg-white/5 backdrop-blur-sm">
            <ShieldCheck className="w-3.5 h-3.5 text-[#0F62FE] dark:text-[#5C97FF]" strokeWidth={1.75} />
            <span className="font-label text-[10px] uppercase tracking-[0.25em] text-black/60 dark:text-white/60">Security · Privacy · Governance</span>
          </div>
          <h1 className="font-display font-semibold leading-[1.2] tracking-tight text-3xl sm:text-4xl lg:text-5xl mb-8">
            Nova <span className="italic text-black/85 dark:text-white/90">Trust Center</span>
          </h1>
          <p className="font-body text-base md:text-lg text-black/70 dark:text-white/70 max-w-2xl mx-auto leading-relaxed mb-10">
            Learn how Nova protects customer data, secures integrations, enables AI-powered compliance automation, and supports modern security, privacy, and governance programs.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/contact')}
              data-testid="trust-contact-security"
              className="font-label bg-[#0F62FE] dark:bg-[#2E79FF] text-white px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] shadow-sm hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] hover:shadow-md transition-all duration-300"
            >
              Contact Security Team
            </button>
            <button
              type="button"
              onClick={openDocModal}
              data-testid="trust-request-docs"
              className="font-label border border-[#0A0A0A]/20 dark:border-white/25 text-[#0A0A0A] dark:text-[#F5F5F5] px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] hover:border-[#0A0A0A] dark:hover:border-white/30 hover:bg-[#D9CFD9]/40 dark:hover:bg-[#1E1724]/40 transition-all duration-500"
            >
              Request Documentation
            </button>
          </div>
        </div>
      </section>

      {/* ===================== SECURITY OVERVIEW ===================== */}
      <section className="px-6 py-24 md:py-32" data-testid="trust-security-overview">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 md:mb-20">
            <Eyebrow>Security Overview</Eyebrow>
            <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight mb-6">Security built into every layer</h2>
            <p className="font-body text-base md:text-lg text-black/65 dark:text-white/65 max-w-2xl mx-auto leading-relaxed">
              The principles that guide how Nova protects your data and your compliance program.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SECURITY_PRINCIPLES.map((p, i) => {
              const Icon = p.icon; const tint = TINTS[i % TINTS.length];
              return (
                <div key={p.title} data-testid={`security-card-${i}`}
                  className="group rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/[0.03] p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: tint + 'AA' }}>
                    <Icon className="w-5 h-5 text-[#0A0A0A] dark:text-[#0A0A0A]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display font-semibold text-lg mb-2">{p.title}</h3>
                  <p className="font-body text-sm text-black/65 dark:text-white/65 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== COMPLIANCE COVERAGE ===================== */}
      <section className="px-6 py-24 md:py-32 bg-[#EBD8C8]/35 dark:bg-[#1C1611]/40 border-y border-black/10 dark:border-white/10" data-testid="trust-compliance">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 md:mb-20">
            <Eyebrow>Compliance &amp; Regulatory Coverage</Eyebrow>
            <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight mb-6">Coverage across leading frameworks</h2>
            <p className="font-body text-base md:text-lg text-black/65 dark:text-white/65 max-w-2xl mx-auto leading-relaxed">
              Nova helps organizations automate and operationalize compliance across leading security, privacy, and AI governance requirements.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 max-w-4xl mx-auto">
            {FRAMEWORKS.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={f.name} data-testid={`framework-card-${i}`}
                  className="rounded-2xl border border-black/10 dark:border-white/10 bg-white/80 dark:bg-white/[0.04] p-7 flex flex-col items-center text-center transition-all duration-500 hover:-translate-y-1 hover:shadow-lg">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-white dark:bg-white/5 border border-black/5 dark:border-white/10">
                    <Icon className="w-5 h-5 text-[#0F62FE] dark:text-[#5C97FF]" strokeWidth={1.5} />
                  </div>
                  <span className="font-display font-semibold text-sm">{f.name}</span>
                </div>
              );
            })}
          </div>
          <p className="mt-8 text-center font-body text-xs text-black/45 dark:text-white/45 max-w-3xl mx-auto leading-relaxed">
            Nova provides tooling to help you prepare for and operationalize these frameworks. Certifications and attestations are issued by independent third-party auditors and certification bodies.
          </p>
        </div>
      </section>

      {/* ===================== AI TRANSPARENCY & GOVERNANCE ===================== */}
      <section className="px-6 py-24 md:py-32" data-testid="trust-ai-governance">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 md:mb-20">
            <Eyebrow>AI Transparency &amp; Governance</Eyebrow>
            <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight mb-6">Responsible AI, by design</h2>
            <p className="font-body text-base md:text-lg text-black/65 dark:text-white/65 max-w-2xl mx-auto leading-relaxed">
              How Nova applies AI to your compliance program — transparently, with you in control.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {AI_GOV.map((c, i) => {
              const Icon = c.icon; const tint = TINTS[i % TINTS.length];
              return (
                <div key={c.title} data-testid={`ai-gov-card-${i}`}
                  className="rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/[0.03] p-7 flex flex-col transition-all duration-500 hover:shadow-lg">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: tint + 'AA' }}>
                    <Icon className="w-5 h-5 text-[#0A0A0A]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display font-semibold text-lg mb-2">{c.title}</h3>
                  <p className="font-body text-sm text-black/65 dark:text-white/65 leading-relaxed">{c.desc}</p>
                  {c.bullets && (
                    <ul className="mt-4 space-y-2">
                      {c.bullets.map((b) => (
                        <li key={b} className="flex items-center gap-2 font-body text-sm text-black/75 dark:text-white/75">
                          <Check className="w-4 h-4 text-[#0F62FE] dark:text-[#5C97FF] shrink-0" strokeWidth={2} />{b}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== INTEGRATIONS SECURITY ===================== */}
      <section className="px-6 py-24 md:py-32 bg-[#D9CFD9]/30 dark:bg-[#161020]/40 border-y border-black/10 dark:border-white/10" data-testid="trust-integrations">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 md:mb-20">
            <Eyebrow>Integrations Security</Eyebrow>
            <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight mb-6">Secure by connection</h2>
            <p className="font-body text-base md:text-lg text-black/65 dark:text-white/65 max-w-2xl mx-auto leading-relaxed">
              Nova connects to external systems using secure authentication, secure credential handling, and only the minimum permissions required.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {INTEGRATIONS.map((it, i) => {
              const Icon = it.icon;
              return (
                <div key={it.name} data-testid={`integration-card-${i}`}
                  className="rounded-2xl border border-black/10 dark:border-white/10 bg-white/80 dark:bg-white/[0.04] p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-white dark:bg-white/5 border border-black/5 dark:border-white/10">
                      <Icon className="w-5 h-5 text-[#0F62FE] dark:text-[#5C97FF]" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-display font-semibold text-xl">{it.name}</h3>
                  </div>
                  <ul className="space-y-3">
                    {it.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 font-body text-sm text-black/75 dark:text-white/75 leading-relaxed">
                        <ShieldCheck className="w-4 h-4 text-[#0F62FE] dark:text-[#5C97FF] shrink-0 mt-0.5" strokeWidth={1.75} />{b}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-center font-body text-xs text-black/50 dark:text-white/50">
            Credentials are handled securely and integrations request only the minimal permissions required to function.
          </p>
        </div>
      </section>

      {/* ===================== DATA HANDLING & PRIVACY ===================== */}
      <section className="px-6 py-24 md:py-32" data-testid="trust-data-handling">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 md:mb-20">
            <Eyebrow>Data Handling &amp; Privacy</Eyebrow>
            <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight mb-6">Your data, handled with care</h2>
            <p className="font-body text-base md:text-lg text-black/65 dark:text-white/65 max-w-2xl mx-auto leading-relaxed">
              How Nova protects compliance information across its full lifecycle.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {DATA_HANDLING.map((d, i) => {
              const Icon = d.icon; const tint = TINTS[i % TINTS.length];
              return (
                <div key={d.title} data-testid={`data-card-${i}`}
                  className="rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/[0.03] p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: tint + 'AA' }}>
                    <Icon className="w-5 h-5 text-[#0A0A0A]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display font-semibold text-lg mb-2">{d.title}</h3>
                  <p className="font-body text-sm text-black/65 dark:text-white/65 leading-relaxed">{d.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== SECURITY ARCHITECTURE ===================== */}
      <section className="px-6 py-24 md:py-32 bg-[#0A0A0A] dark:bg-[#050505] text-[#F5F5F5] border-y border-white/10" data-testid="trust-architecture">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="h-px w-8 bg-[#5C97FF]/50" />
              <p className="font-label text-[13px] font-semibold uppercase tracking-[0.22em] text-[#5C97FF]">Security Architecture</p>
              <span className="h-px w-8 bg-[#5C97FF]/50" />
            </div>
            <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight mb-6">How Nova securely processes compliance data</h2>
            <p className="font-body text-base md:text-lg text-white/65 max-w-2xl mx-auto leading-relaxed">
              Data flows through encrypted, access-controlled layers — from your systems to defensible, audit-ready evidence.
            </p>
          </div>

          {/* Sources row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-2">
            {[{ icon: Building2, label: 'Google Workspace' }, { icon: GitBranch, label: 'GitHub' }, { icon: Network, label: 'Future Integrations' }].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="rounded-xl border border-white/15 bg-white/[0.04] px-5 py-4 flex items-center gap-3">
                  <Icon className="w-5 h-5 text-[#5C97FF] shrink-0" strokeWidth={1.5} />
                  <span className="font-display font-semibold text-sm">{s.label}</span>
                </div>
              );
            })}
          </div>

          {ARCH_LAYERS.map((layer, i) => {
            const Icon = layer.icon;
            return (
              <React.Fragment key={layer.title}>
                <div className="flex justify-center py-3"><ChevronDown className="w-5 h-5 text-white/40" /></div>
                <div data-testid={`arch-layer-${i}`}
                  className="rounded-xl border border-white/15 bg-gradient-to-r from-white/[0.06] to-white/[0.02] p-5 md:p-6 flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[#0F62FE]/20 border border-[#0F62FE]/30 shrink-0">
                    <Icon className="w-5 h-5 text-[#5C97FF]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-lg mb-1">{layer.title}</h3>
                    <p className="font-body text-sm text-white/65 leading-relaxed">{layer.desc}</p>
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* Highlights */}
          <div className="mt-12 flex flex-wrap justify-center gap-2.5">
            {ARCH_HIGHLIGHTS.map((h) => (
              <span key={h} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] px-3.5 py-1.5 font-label text-[10px] uppercase tracking-[0.15em] text-white/70">
                <Check className="w-3 h-3 text-[#5C97FF]" strokeWidth={2.5} />{h}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== RESPONSIBLE DISCLOSURE ===================== */}
      <section className="px-6 py-24 md:py-28" data-testid="trust-disclosure">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/[0.03] p-8 md:p-12">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-[#0F62FE]/10 dark:bg-[#2E79FF]/15 border border-[#0F62FE]/20 shrink-0">
                <ShieldAlert className="w-6 h-6 text-[#0F62FE] dark:text-[#5C97FF]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <Eyebrow>Responsible Disclosure</Eyebrow>
                <h2 className="font-display font-semibold text-2xl md:text-3xl tracking-tight mb-3">Report a vulnerability</h2>
                <p className="font-body text-sm md:text-base text-black/70 dark:text-white/70 leading-relaxed mb-5">
                  If you believe you have identified a potential security vulnerability, please contact our security team. We appreciate responsible disclosure and work to investigate and address reports appropriately.
                </p>
                <a href="mailto:security@teamnova.ai" data-testid="trust-disclosure-email"
                  className="inline-flex items-center gap-2 font-label text-xs uppercase tracking-[0.18em] text-[#0F62FE] dark:text-[#5C97FF] hover:underline">
                  <Mail className="w-4 h-4" strokeWidth={1.75} /> security@teamnova.ai
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== POLICIES & DOCUMENTATION ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="trust-policies">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <Eyebrow>Policies &amp; Documentation</Eyebrow>
            <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight">Resource center</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {POLICIES.map((p, i) => {
              const Icon = p.icon;
              const handle = () => { p.href ? (window.location.href = p.href) : navigate(p.to); };
              return (
                <button key={p.title} type="button" onClick={handle} data-testid={`policy-card-${i}`}
                  className="group text-left rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/[0.03] p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-lg">
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-white dark:bg-white/5 border border-black/5 dark:border-white/10">
                      <Icon className="w-5 h-5 text-[#0F62FE] dark:text-[#5C97FF]" strokeWidth={1.5} />
                    </div>
                    <ArrowRight className="w-4 h-4 text-black/30 dark:text-white/30 group-hover:translate-x-1 group-hover:text-[#0F62FE] dark:group-hover:text-[#5C97FF] transition-all duration-300" />
                  </div>
                  <h3 className="font-display font-semibold text-base mb-1.5">{p.title}</h3>
                  <p className="font-body text-sm text-black/60 dark:text-white/60 leading-relaxed">{p.desc}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section className="px-6 py-24 md:py-32 bg-[#D4E0DA]/45 dark:bg-[#101F18]/45 border-y border-black/10 dark:border-white/10" data-testid="trust-faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <Eyebrow>Frequently Asked Questions</Eyebrow>
            <h2 className="font-display font-semibold text-3xl md:text-4xl leading-[1.1] tracking-tight">Questions, answered</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={f.q} data-testid={`faq-item-${i}`}
                  className="rounded-2xl border border-black/10 dark:border-white/10 bg-white/80 dark:bg-white/[0.03] overflow-hidden">
                  <button type="button" onClick={() => setOpenFaq(open ? -1 : i)} data-testid={`faq-trigger-${i}`}
                    aria-expanded={open}
                    className="w-full flex items-center justify-between gap-4 text-left px-6 py-5">
                    <span className="font-display font-semibold text-base">{f.q}</span>
                    <ChevronDown className={`w-5 h-5 shrink-0 text-black/45 dark:text-white/45 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`grid transition-all duration-300 ease-in-out ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                    <div className="overflow-hidden">
                      <p className="font-body text-sm text-black/70 dark:text-white/70 leading-relaxed px-6 pb-5">{f.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== CLOSING CTA ===================== */}
      <section className="px-6 py-24 md:py-32 bg-[#0A0A0A] dark:bg-[#050505] text-[#F5F5F5]" data-testid="trust-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display font-semibold text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight mb-6">
            Have security or compliance questions?
          </h2>
          <p className="font-body text-base md:text-lg text-white/65 leading-relaxed mb-10 max-w-xl mx-auto">
            Our team is happy to walk you through Nova's security architecture, data handling, and AI governance practices.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button type="button" onClick={() => navigate('/contact')} data-testid="trust-cta-contact"
              className="font-label bg-[#0F62FE] dark:bg-[#2E79FF] text-white px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] shadow-sm hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] hover:shadow-md transition-all duration-500">
              Contact Security Team
            </button>
            <button type="button" onClick={openDocModal} data-testid="trust-cta-docs"
              className="font-label border border-white/25 bg-transparent text-[#FAFAFA] px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] hover:bg-white/10 transition-all duration-500">
              Request Documentation
            </button>
          </div>
        </div>
      </section>

      {/* ===================== REQUEST DOCUMENTATION MODAL ===================== */}
      <Dialog open={docOpen} onOpenChange={setDocOpen}>
        <DialogContent className="max-w-lg p-0 overflow-hidden bg-[#FAFAFA] dark:bg-[#0A0A0A] border border-black/10 dark:border-white/10" data-testid="trust-doc-modal">
          {submitted ? (
            <div className="p-10 text-center" data-testid="trust-doc-success">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 bg-[#0F62FE]/10 dark:bg-[#2E79FF]/15 border border-[#0F62FE]/20">
                <CheckCircle2 className="w-7 h-7 text-[#0F62FE] dark:text-[#5C97FF]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display font-semibold text-2xl tracking-tight mb-3 text-[#0A0A0A] dark:text-[#F5F5F5]">Request received</h3>
              <p className="font-body text-sm text-black/65 dark:text-white/65 leading-relaxed max-w-sm mx-auto mb-7">
                Thank you. Our security team will review your request and follow up at the email you provided with the appropriate documentation.
              </p>
              <button type="button" onClick={() => setDocOpen(false)} data-testid="trust-doc-close"
                className="font-label bg-[#0F62FE] dark:bg-[#2E79FF] text-white px-7 py-3 rounded-full text-xs uppercase tracking-[0.2em] hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] transition-all duration-300">
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={submitDocRequest} className="p-8" data-testid="trust-doc-form">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <p className="font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-[#0F62FE] dark:text-[#5C97FF] mb-2">Request Documentation</p>
                  <h3 className="font-display font-semibold text-2xl tracking-tight text-[#0A0A0A] dark:text-[#F5F5F5]">Get Nova's security materials</h3>
                </div>
              </div>
              <p className="font-body text-sm text-black/60 dark:text-white/60 leading-relaxed mb-6">
                Tell us a little about you and we'll share the right documentation.
              </p>

              <div className="space-y-4">
                {[
                  { k: 'full_name', label: 'Full Name', type: 'text', required: true, placeholder: 'Jane Doe' },
                  { k: 'company_name', label: 'Company Name', type: 'text', required: false, placeholder: 'Acme Inc.' },
                  { k: 'work_email', label: 'Work Email', type: 'email', required: true, placeholder: 'jane@acme.com' },
                  { k: 'role_title', label: 'Role / Job Title', type: 'text', required: false, placeholder: 'Security Engineer' },
                ].map((f) => (
                  <div key={f.k}>
                    <label className="block font-label text-[11px] uppercase tracking-[0.15em] text-black/55 dark:text-white/55 mb-1.5">
                      {f.label}{f.required && <span className="text-[#0F62FE] dark:text-[#5C97FF]"> *</span>}
                    </label>
                    <input
                      type={f.type} required={f.required} placeholder={f.placeholder}
                      value={form[f.k]} onChange={(e) => setForm((p) => ({ ...p, [f.k]: e.target.value }))}
                      data-testid={`trust-doc-${f.k}`}
                      className="w-full rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/[0.04] px-4 py-2.5 text-sm text-[#0A0A0A] dark:text-[#F5F5F5] placeholder:text-black/35 dark:placeholder:text-white/30 focus:outline-none focus:border-[#0F62FE] dark:focus:border-[#5C97FF] focus:ring-2 focus:ring-[#0F62FE]/15 transition-all"
                    />
                  </div>
                ))}
                <div>
                  <label className="block font-label text-[11px] uppercase tracking-[0.15em] text-black/55 dark:text-white/55 mb-1.5">Documentation Requested</label>
                  <select
                    value={form.documentation_requested}
                    onChange={(e) => setForm((p) => ({ ...p, documentation_requested: e.target.value }))}
                    data-testid="trust-doc-documentation_requested"
                    className="w-full rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/[0.04] px-4 py-2.5 text-sm text-[#0A0A0A] dark:text-[#F5F5F5] focus:outline-none focus:border-[#0F62FE] dark:focus:border-[#5C97FF] focus:ring-2 focus:ring-[#0F62FE]/15 transition-all"
                  >
                    {DOC_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>

              <button type="submit" disabled={submitting} data-testid="trust-doc-submit"
                className="mt-7 w-full font-label bg-[#0F62FE] dark:bg-[#2E79FF] text-white px-8 py-3.5 rounded-full text-xs uppercase tracking-[0.2em] shadow-sm hover:bg-[#0A4FCC] dark:hover:bg-[#5C97FF] hover:shadow-md transition-all duration-300 disabled:opacity-60 inline-flex items-center justify-center gap-2">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? 'Submitting…' : 'Request Documentation'}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default Trust;
